import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import TasksModule from '@modules/TasksModule';
import {
  CallEvent,
  PcsConversationFetchedEvent,
  WebphoneCallEvent,
} from '@modules/SoftPhoneModule/types';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import ServiceModule from '@modules/ServiceModule';
import { SpeedToLeadTaskView } from './components/SpeedToLeadTaskView';
import { SpeedToLeadTaskRow } from './components/SpeedToLeadTaskRow';
import {
  SPEED_TO_LEAD_OUTBOUND_TASK_TYPE,
  SpeedToLeadTaskAttributes,
} from './types';
import handleStlCallAnswered from './events/handleStlCallAnswered';
import handleStlCallEnded from './events/handleStlCallEnded';
import handleStlCallFailed from './events/handleStlCallFailed';
import handleStlPcsConversationFetched from './events/handleStlPcsConversationFetched';
import handleStlPreDialRelease from './events/handleStlPreDialRelease';

class SpeedToLeadTasksModule extends AppModule {
  override events = []; // this module emits no events; it only listens to events from other modules

  override preload(): void {
    // Registered unconditionally — the feature flag (featureSpeedToLeadOutboundingV0)
    // is enforced in the UI (TaskList, InboxPage), so registering when the flag is off
    // is harmless: no tasks of this type can be claimed and nothing surfaces the view.
    // Gating here caused a bug where isEnabled() always returned false at preload time
    // because LaunchDarkly initializes asynchronously after sign-in.
    TasksModule.registerTaskDefinition({
      key: 'speed-to-lead',
      category: 'Outbound',
      types: [SPEED_TO_LEAD_OUTBOUND_TASK_TYPE],
      taskRowComponent: SpeedToLeadTaskRow,
      taskViewComponent: SpeedToLeadTaskView,
    });

    // On session restore (page refresh / sign-out+back-in), any pre-dial STL task
    // rehydrated from local storage is stale — its acceptance expiry timer did not
    // survive the page unload. Delete it locally so the CSR isn't looking at a task
    // with no countdown running. Backend cleanup is handled by the BFF cron job (task 4b).
    this.clearStalePreDialTasks();

    this.app.events.on('phone.call.answered', (ev: CallEvent) =>
      handleStlCallAnswered(ev),
    );

    this.app.events.on('phone.call.ended', (ev: CallEvent) =>
      handleStlCallEnded(ev),
    );

    this.app.events.on(
      'phone.call.failed-general',
      (ev: WebphoneCallEvent) =>
        void handleStlCallFailed(ev).catch((err) =>
          this.app.logger.error('stl:call-failed:unhandled', { err }),
        ),
    );

    this.app.events.on(
      'phone.pcs.conversation.fetched',
      (ev: PcsConversationFetchedEvent) => handleStlPcsConversationFetched(ev),
    );

    // Pre-dial task release on inbound call.
    // When an inbound call arrives while the CSR has a claimed but un-dialled
    // STL task, release the task so the CSR can focus on the inbound call.
    //
    // Two subscriptions cover the same scenario from different systems so that
    // a failure in one pathway does not leave the task stuck. Both come from
    // independent channels, so they fail independently.
    //
    // handleStlPreDialRelease is idempotent: cancelAcceptanceExpiry() returns null
    // after the first invocation clears the state, so subsequent calls are no-ops.
    // session.offered fires for every agent in the system, not just the current CSR.
    // Guard against userId so we only release when the inbound call is offered to this CSR.
    this.app.events.on('session.offered', (ev: { userId: string }) => {
      if (ev.userId === ServiceModule.getCurrentUserId()) {
        void handleStlPreDialRelease('inbound-offered');
      }
    });

    this.app.events.on('phone.call.placed', (ev: CallEvent) => {
      if (ev.terminating) {
        void handleStlPreDialRelease('inbound-call-placed');
      }
    });

    // Pre-dial task release when the CSR goes offline.
    // Reuses the same handler — behavior is identical to the inbound path.
    this.app.events.on<{ status: UserSetAvailabilityStatus }>(
      'user.status.updated',
      (ev) => {
        if (ev.status === UserSetAvailabilityStatus.Offline) {
          void handleStlPreDialRelease('csr-went-offline');
        }
      },
    );
  }

  private clearStalePreDialTasks(): void {
    // Safe to call here: TasksModule is registered before SpeedToLeadTasksModule
    // in src/modules/index.ts, so its preload() (which synchronously reads
    // localStorage) has already completed by the time this runs.
    const stale = TasksModule.getTasks().filter(
      (task) =>
        task.type === SPEED_TO_LEAD_OUTBOUND_TASK_TYPE &&
        !(task.attributes as SpeedToLeadTaskAttributes).hasMadeCall,
    );

    if (stale.length === 0) return;

    this.app.logger.info('stl:restore:stale-pre-dial-tasks-cleared', {
      count: stale.length,
      taskIds: stale.map((t) => t.id),
    });

    stale.forEach((task) => TasksModule.deleteTask(task.id));
  }
}

export default new SpeedToLeadTasksModule(
  appManager.events,
  appManager.logger('SpeedToLead'),
);
